/**
 * Tenant Store - Multi-tenant account management for Universal Dashboard
 *
 * Manages selected tenant state, gateway token, and tenant-scoped tokens.
 * Persists the selected tenant across page refreshes.
 */
import { create } from "zustand";
import { persist } from "zustand/middleware";

export interface TenantAccount {
  tenant_id: string;
  tenant_name: string;
  tenant_slug: string;
  api_url: string;
  venue_name: string;
  role: string;
  status: string;
}

interface TenantState {
  // Gateway token (short-lived, used during tenant selection)
  gatewayToken: string | null;

  // Available accounts after login
  accounts: TenantAccount[];

  // Currently selected tenant
  selectedTenant: TenantAccount | null;

  // User email from central auth
  email: string | null;

  // Actions
  setGatewayAuth: (
    token: string,
    accounts: TenantAccount[],
    email: string,
  ) => void;
  selectTenant: (
    tenant: TenantAccount,
    access: string,
    refresh: string,
  ) => void;
  clearTenant: () => void;
  clearAll: () => void;
  getApiBaseUrl: () => string;
}

const useTenantStore = create<TenantState>()(
  persist(
    (set, get) => ({
      gatewayToken: null,
      accounts: [],
      selectedTenant: null,
      email: null,

      setGatewayAuth: (token, accounts, email) =>
        set({ gatewayToken: token, accounts, email }),

      selectTenant: (tenant, access, refresh) => {
        localStorage.setItem("accessToken", access);
        localStorage.setItem("refreshToken", refresh);
        set({ selectedTenant: tenant, gatewayToken: null });
      },

      clearTenant: () => {
        localStorage.removeItem("accessToken");
        localStorage.removeItem("refreshToken");
        set({ selectedTenant: null });
      },

      clearAll: () => {
        localStorage.removeItem("accessToken");
        localStorage.removeItem("refreshToken");
        set({
          gatewayToken: null,
          accounts: [],
          selectedTenant: null,
          email: null,
        });
      },

      getApiBaseUrl: () => {
        const { selectedTenant } = get();
        if (selectedTenant?.api_url) {
          // Ensure trailing slash
          const url = selectedTenant.api_url;
          return url.endsWith("/") ? url : `${url}/`;
        }
        // Fallback to env variable (legacy single-tenant mode)
        return import.meta.env.VITE_APP_BASE_URL;
      },
    }),
    {
      name: "tenant-store",
      partialize: (state) => ({
        selectedTenant: state.selectedTenant,
        accounts: state.accounts,
        email: state.email,
      }),
    },
  ),
);

export default useTenantStore;
